import { authenticatedFastApiAxiosInstanceAllowEmptyString } from "@/lib/data/http";
import { baseFastApiMatterRoute } from "@/lib/data/requests/matters/matter";
import type { MatterFileParentInput } from "@/lib/data/requests/matters/matter-file";
import type { MatterFile, APIResponse, MatterTemplate } from "@/lib/types";

const MAX_MATTER_TEMPLATE_PAGES = 50;
const DEFAULT_MATTER_TEMPLATE_LIMIT = 100;

type RetrieveMatterTemplatesInput = {
  cursor?: string;
  limit?: number;
};

const getNextTemplateCursor = (response: APIResponse<MatterTemplate[]>) => {
  const pagination = response.pagination as
    | { cursor?: string; lastEvaluatedKey?: string; nextCursor?: string }
    | undefined;
  return (
    pagination?.cursor ?? pagination?.nextCursor ?? pagination?.lastEvaluatedKey
  );
};

export async function retrieveMatterTemplates({
  limit = DEFAULT_MATTER_TEMPLATE_LIMIT,
}: RetrieveMatterTemplatesInput = {}) {
  let cursor: string | undefined;
  let pageCount = 0;
  let latestMeta: APIResponse<MatterTemplate[]>["meta"] = { userMsg: "" };
  const templates: MatterTemplate[] = [];

  do {
    const response =
      await authenticatedFastApiAxiosInstanceAllowEmptyString.get<
        APIResponse<MatterTemplate[]>
      >(`${baseFastApiMatterRoute}/templates`, {
        params: {
          limit,
          ...(cursor ? { cursor } : {}),
        },
      });

    templates.push(...response.data.data);
    latestMeta = response.data.meta;
    cursor = getNextTemplateCursor(response.data);
    pageCount += 1;
  } while (cursor && pageCount < MAX_MATTER_TEMPLATE_PAGES);

  return {
    data: templates,
    meta: latestMeta,
  } satisfies APIResponse<MatterTemplate[]>;
}

export type CreateMatterTemplateInput = { operationId?: string } & Pick<
  MatterTemplate,
  "name"
> &
  Partial<
    Pick<
      MatterTemplate,
      | "category"
      | "description"
      | "moduleShortcut"
      | "presignedUrlKey"
      | "type"
      | "variables"
    >
  >;

export const createMatterTemplate = (data: CreateMatterTemplateInput) => {
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .post<
      APIResponse<MatterTemplate>
    >(`${baseFastApiMatterRoute}/templates`, data)
    .then((res) => res.data);
};

export type UpdateMatterTemplateInput = Pick<
  MatterTemplate,
  "dateModified" | "id"
> &
  Partial<CreateMatterTemplateInput>;

export const updateMatterTemplate = (data: UpdateMatterTemplateInput) => {
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .patch<
      APIResponse<MatterTemplate>
    >(`${baseFastApiMatterRoute}/templates`, data)
    .then((res) => res.data);
};

export const deleteMatterTemplate = (data: { id: string }) => {
  const { id } = data;
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .delete<
      APIResponse<MatterTemplate>
    >(`${baseFastApiMatterRoute}/templates/${id}`)
    .then((res) => res.data);
};

type GenerateMatterTemplateDocumentBaseInput = {
  massTortId?: string;
  matterFileName: string;
  matterFileIdLocation: string | null | undefined;
  matterFileExtension: ".docx" | ".pdf";
} & MatterFileParentInput;

export type GenerateMatterTemplateDocumentInput =
  | (GenerateMatterTemplateDocumentBaseInput & {
      id: MatterTemplate["id"];
      matterTemplateInputs: { [key: string]: string };
      matterFileId?: never;
      matterFileInputs?: never;
    })
  | (GenerateMatterTemplateDocumentBaseInput & {
      matterFileId: MatterFile["id"];
      matterFileInputs: { [key: string]: string };
      id?: never;
      matterTemplateInputs?: never;
    });

const removeUndefinedValues = (data: Record<string, unknown>) =>
  Object.fromEntries(
    Object.entries(data).filter(([, value]) => value !== undefined),
  );

const serializeGenerateMatterTemplateDocumentPayload = (
  data: GenerateMatterTemplateDocumentInput,
) =>
  removeUndefinedValues({
    extension: data.matterFileExtension,
    fileId: data.matterFileId,
    id: data.id,
    inputs: data.matterTemplateInputs ?? data.matterFileInputs,
    idLocation: data.matterFileIdLocation ?? "",
    massTortId: data.massTortId,
    matterId: data.matterId,
    name: data.matterFileName,
  });

export const generateMatterTemplateDocument = (
  data: GenerateMatterTemplateDocumentInput,
) => {
  return authenticatedFastApiAxiosInstanceAllowEmptyString
    .post<
      APIResponse<MatterFile>
    >(`${baseFastApiMatterRoute}/templates/generate`, serializeGenerateMatterTemplateDocumentPayload(data))
    .then((res) => res.data);
};
